import { useEffect, useState, type ReactNode } from "react";
import { AppShell, Panel, SidebarNav, type SidebarNavSection } from "@legends/ui";
import { logout, type AssignedEvent, type AuthUser, type CaveEvent } from "./api";
// Match the admin language across the operator experience (see CaveConsoleScreen).
import "./admin/adminTheme.css";
import {
  LegendsBrand,
  ShellTitle,
  SidebarUser,
  formatDateTime,
  monoLabelStyle,
} from "./sections";
import DirectorConsole from "./DirectorConsole";

// Event-only Director entry point (ADR 0065). An operator granted production on a
// single event (event_config, ADR 0038) holds no cave_config, so the cave-console
// landing (which keys off GET /caves) never surfaces for them — leaving the
// assignee stranded on a persona placeholder or the storefront. This screen is
// their usable entry point: it lists the CaveCasts they're assigned to and opens
// the SAME per-event DirectorConsole the cave console's Events tab mounts. It is a
// labelled, EPHEMERAL entry scoped to one show — not full cave chrome, since the
// grant confers no cave-wide config.

/** The minimal CaveEvent shape DirectorConsole needs (it reads id + title). */
export function assignedToCaveEvent(e: AssignedEvent): CaveEvent {
  return { id: e.eventId, title: e.title, startsAt: e.startsAt, durationMinutes: 0 };
}

export interface AssignedEventsScreenProps {
  events: AssignedEvent[];
  user: AuthUser;
  onLogout: () => void;
  /** Impersonation strip, rendered in the shell's banner slot (see App.tsx). */
  banner?: ReactNode;
}

export default function AssignedEventsScreen({
  events,
  user,
  onLogout,
  banner,
}: AssignedEventsScreenProps) {
  const [selectedId, setSelectedId] = useState<string>(events[0]?.eventId ?? "");
  const selected = events.find((e) => e.eventId === selectedId) ?? events[0];

  // Adopt the admin language for the event-only operator entry too (see CaveConsoleScreen).
  useEffect(() => {
    document.body.classList.add("admin-scope");
    return () => document.body.classList.remove("admin-scope");
  }, []);

  const signOut = () => {
    logout().finally(onLogout);
  };

  const sections: SidebarNavSection[] = [
    {
      title: "ASSIGNED CAVECASTS",
      items: events.map((e) => ({
        label: e.title,
        icon: "▤",
        active: e.eventId === selected?.eventId,
        onClick: () => setSelectedId(e.eventId),
      })),
    },
  ];

  return (
    <AppShell
      brand={<LegendsBrand />}
      title={
        <ShellTitle>
          {selected ? `${selected.caveName} · ${selected.title}` : "Assigned CaveCasts"}
        </ShellTitle>
      }
      sidebar={<SidebarNav sections={sections} />}
      sidebarFooter={
        <SidebarUser name={user.displayName || user.username} onSignOut={signOut} />
      }
      banner={banner}
    >
      <div style={{ maxWidth: 900, display: "grid", gap: 14 }}>
        <Panel tier="quiet" aria-label="Event assignment">
          <p style={{ margin: 0, fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}>
            You're assigned to direct this CaveCast.
          </p>
          <p style={{ margin: "6px 0 0", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
            This is an event assignment — direction access scoped to this one show, not full
            cave management.
          </p>
          {selected && (
            <p style={{ ...monoLabelStyle, margin: "10px 0 0" }}>
              {selected.caveName} · {formatDateTime(selected.startsAt)}
            </p>
          )}
        </Panel>
        {selected ? (
          <DirectorConsole
            key={selected.eventId}
            caveId={selected.caveId}
            event={assignedToCaveEvent(selected)}
          />
        ) : (
          <p style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
            No assigned CaveCasts.
          </p>
        )}
      </div>
    </AppShell>
  );
}
